import * as R from 'ramda';
import { graphql } from '~/apollo/generated/v4';
import type { OutcropPaleogeographyPartsFragment } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import { Well } from '~/components/common/Well';
import {
  AgeTable,
  hasAnyAgeValues,
} from '~/components/geologicalAge/FullAgePopover';
import { PaleomapListItem } from '~/components/outcrop/paleomap-display-v4/paleomap-list-item';
import { WikiPopover } from '~/components/wiki/WikiPopover';
import { hasValue } from '~/utils/common';

export const outcropPaleogeographyParts = graphql(`
  fragment outcropPaleogeographyParts on Outcrop {
    ...outcropParts
    paleoMaps {
      ...paleoMapParts
      pictures {
        ...pictureParts
        pictureUrl
        thumbnailUrl: pictureUrl(width: 500)
        file {
          ...fileParts
        }
      }
    }
    startAge {
      ...lithostratAgeParts
    }
    endAge {
      ...lithostratAgeParts
    }
  }
`);

type Outcrop = OutcropPaleogeographyPartsFragment;

function hasAnySourceAreaValues(outcrop: Outcrop) {
  return R.any(hasValue, [
    outcrop.sourceAreaName,
    outcrop.sourceAreaDescription,
    outcrop.sourceAreaGeography,
    outcrop.sourceAreaStructure,
    outcrop.sourceAreaClimate,
    outcrop.sourceAreaDistanceToSourceAreaDesc,
    outcrop.sourceAreaDistanceToShorelineDesc,
    outcrop.sourceAreaDistanceToShelfEdgeDesc,
    outcrop.sourceAreaComments,
  ]);
}

function hasAnyBasinValues(outcrop: Outcrop) {
  return R.any(hasValue, [
    outcrop.basinName,
    outcrop.basinType,
    outcrop.basinCatchmentArea,
    outcrop.basinTerrestrialMarine,
    outcrop.basinDescription,
  ]);
}

function NonNullRow({
  label,
  value,
  unit,
  otherWikiId,
}: {
  label: string;
  value: string | null | undefined;
  unit?: string;
  otherWikiId?: number;
}) {
  if (!hasValue(value)) {
    return null;
  }

  return (
    <tr>
      <td>
        <strong>
          {label}
          {otherWikiId && (
            <>
              {' '}
              <WikiPopover otherWikiId={38} />
            </>
          )}
        </strong>
      </td>
      <td>
        {value}
        {unit && ` ${unit}`}
      </td>
    </tr>
  );
}

export function PaleomapDisplay({
  outcrop,
}: {
  outcrop: OutcropPaleogeographyPartsFragment;
}) {
  const hasSourceArea = hasAnySourceAreaValues(outcrop);
  const hasBasin = hasAnyBasinValues(outcrop);

  const paleoMaps = outcrop.paleoMaps;
  const hasPaleoMaps = paleoMaps.length > 0;

  const twoColLayout = hasSourceArea && hasBasin ? 6 : 12;

  const hasStartAge = outcrop.startAge && hasAnyAgeValues(outcrop.startAge);
  const hasEndAge = outcrop.endAge && hasAnyAgeValues(outcrop.endAge);
  const hasBothAges = hasStartAge && hasEndAge;

  return (
    <div className="space-y-4">
      {hasPaleoMaps && (
        <>
          {paleoMaps.map(paleoMap => (
            <PaleomapListItem
              key={paleoMap.id}
              outcropId={parseInt(outcrop.id)}
              paleoMap={paleoMap}
            />
          ))}
        </>
      )}

      {(hasAnyAgeValues(outcrop.startAge) ||
        hasAnyAgeValues(outcrop.endAge)) && (
        <Well>
          <Heading level={3}>Geological Age</Heading>

          <div className="grid lg:grid-cols-2 gap-6">
            {outcrop.startAge && hasAnyAgeValues(outcrop.startAge) && (
              <div>
                {hasBothAges && <Heading level={5}>Start</Heading>}
                <AgeTable age={outcrop.startAge} />
              </div>
            )}
            {outcrop.endAge && hasAnyAgeValues(outcrop.endAge) && (
              <div>
                {hasBothAges && <Heading level={5}>End</Heading>}
                <AgeTable age={outcrop.endAge} />
              </div>
            )}
          </div>
        </Well>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {hasSourceArea && (
          <div className={twoColLayout ? '' : 'lg:col-span-2'}>
            <Well>
              <Heading level={3}>Source area information</Heading>

              <table className="table table-compact w-full mt-2">
                <tbody>
                  <NonNullRow label="Name" value={outcrop.sourceAreaName} />
                  <NonNullRow
                    label="Source area geography"
                    value={outcrop.sourceAreaGeography}
                  />
                  <NonNullRow
                    label="Source area structure"
                    value={outcrop.sourceAreaStructure}
                  />
                  <NonNullRow
                    label="Source area climate"
                    value={outcrop.sourceAreaClimate}
                  />
                  <NonNullRow
                    label="Estimated distance to source area"
                    value={outcrop.sourceAreaDistanceToSourceAreaDesc}
                    otherWikiId={38}
                  />
                  <NonNullRow
                    label="Estimated distance to shoreline"
                    value={outcrop.sourceAreaDistanceToShorelineDesc}
                    otherWikiId={38}
                  />
                  <NonNullRow
                    label="Estimated distance to shelf edge"
                    value={outcrop.sourceAreaDistanceToShelfEdgeDesc}
                    otherWikiId={38}
                  />

                  {!R.isNil(outcrop.sourceAreaDescription) && (
                    <tr>
                      <td colSpan={2}>
                        <div>
                          <strong>Description</strong>
                        </div>
                        <div className="break-words whitespace-pre-line">
                          {outcrop.sourceAreaDescription}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Well>
          </div>
        )}

        {hasBasin && (
          <div className={twoColLayout ? '' : 'lg:col-span-2'}>
            <Well>
              <Heading level={3}>Basin information</Heading>
              <table className="table table-compact w-full mt-2">
                <tbody>
                  <NonNullRow label="Basin name" value={outcrop.basinName} />
                  <NonNullRow label="Basin type" value={outcrop.basinType} />
                  <NonNullRow
                    label="Catchment area"
                    value={outcrop.basinCatchmentArea?.toString()}
                    unit="km²"
                  />
                  <NonNullRow
                    label="Terrestrial/Marine"
                    value={outcrop.basinTerrestrialMarine}
                  />

                  {outcrop.basinDescription && (
                    <tr>
                      <td colSpan={2}>
                        <div>
                          <strong>Description</strong>
                        </div>
                        <div className="break-words whitespace-pre-line">
                          {outcrop.basinDescription}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Well>
          </div>
        )}
      </div>
    </div>
  );
}
